import { useEffect, useMemo, useState } from "react"
import { Link, useNavigate, useParams } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { ROUTES } from '@/constants/routes'
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Textarea } from "@/components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { LatexText } from "@/components/ui/LatexText"
import { deleteMistake, disputeAttempt, fetchMistakeDetail, MistakeRecordDetail, MistakeStatus, updateMistake } from "@/api/mistakes"
import { generateFromMistakes } from "@/api/flashcards"
import { toast } from "sonner"
import { Layers } from "lucide-react"
import { useLookupStore } from "@/store/lookupStore"

function AnswerDisplay({ answer }: { answer: Record<string, unknown> | null | undefined }) {
  if (!answer) return <span className="text-slate-400">暂无</span>
  const value = (answer.value || answer.text) as string | undefined
  const images = answer.images as string[] | undefined
  if (!value && (!images || images.length === 0)) return <span className="text-slate-400">暂无</span>
  return (
    <div className="space-y-2">
      {value && <LatexText>{value}</LatexText>}
      {images && images.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {images.map((src, i) => (
            <img key={i} src={src} alt={`图片 ${i + 1}`} className="max-w-sm max-h-60 rounded border border-slate-200" />
          ))}
        </div>
      )}
    </div>
  )
}

const statusLabels: Record<MistakeStatus, string> = {
  unreviewed: "未复盘",
  reviewed: "已复盘",
  mastered: "已掌握",
}

const disputeReasonLabels: Record<string, string> = {
  answer_correct: "我的答案其实正确",
  question_error: "题目本身有问题",
  ai_grading_bug: "AI批改逻辑有误",
  ambiguous: "题目有歧义",
}

export default function MistakeDetailPage() {
  const { mistakeId } = useParams<{ mistakeId: string }>()
  const navigate = useNavigate()
  const [record, setRecord] = useState<MistakeRecordDetail | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [status, setStatus] = useState<MistakeStatus | "">("")
  const [remark, setRemark] = useState("")
  const [selectedErrorReasons, setSelectedErrorReasons] = useState<string[]>([])
  const [saving, setSaving] = useState(false)
  const [addingFlashcard, setAddingFlashcard] = useState(false)
  const [tip, setTip] = useState<string | null>(null)
  const [disputingAttemptId, setDisputingAttemptId] = useState<string | null>(null)
  const [disputeReason, setDisputeReason] = useState<string>("")

  const { subjects, errorReasons: lookupErrorReasons, fetchAll } = useLookupStore()

  const normalizedId = useMemo(() => mistakeId || "", [mistakeId])

  useEffect(() => {
    fetchAll()
  }, [fetchAll])

  useEffect(() => {
    if (!normalizedId) return
    let mounted = true
    setLoading(true)
    setError(null)
    fetchMistakeDetail(normalizedId)
      .then((res) => {
        if (!mounted) return
        setRecord(res.record)
        setStatus(res.record.status)
        setRemark(res.record.remark || "")
        // Load error reasons from the most recent wrong attempt
        const lastWrong = res.record.attempts.find(a => !a.isCorrect)
        setSelectedErrorReasons(lastWrong?.errorReasons || [])
      })
      .catch((err) => {
        if (!mounted) return
        setError(err instanceof Error ? err.message : String(err))
      })
      .finally(() => {
        if (!mounted) return
        setLoading(false)
      })
    return () => {
      mounted = false
    }
  }, [normalizedId])

  async function handleSave() {
    if (!record) return
    setSaving(true)
    setTip(null)
    try {
      const res = await updateMistake(record.id, {
        status: status || undefined,
        remark,
        errorReasons: selectedErrorReasons,
      })
      setRecord(res.record)
      setStatus(res.record.status)
      setRemark(res.record.remark || "")
      setTip("已保存")
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setSaving(false)
    }
  }

  async function handleDispute(attemptId: string) {
    if (!record || !disputeReason) return
    setSaving(true)
    setTip(null)
    try {
      const res = await disputeAttempt(record.id, attemptId, disputeReason)
      setRecord(res.record)
      setStatus(res.record.status)
      setDisputingAttemptId(null)
      setDisputeReason("")
      setTip("申诉已提交")
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete() {
    if (!record) return
    if (!window.confirm("确定删除这条错题吗？")) return
    setSaving(true)
    setTip(null)
    try {
      await deleteMistake(record.id)
      setTip("已删除，可在回收站恢复")
      navigate(ROUTES.MISTAKES)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setSaving(false)
    }
  }

  function toggleErrorReason(code: string) {
    setSelectedErrorReasons(prev =>
      prev.includes(code) ? prev.filter(r => r !== code) : [...prev, code]
    )
  }

  if (!normalizedId) {
    return (
      <div className="min-h-screen bg-slate-50 text-slate-900">
        <div className="mx-auto max-w-5xl px-6 py-8">无效的错题记录</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <div className="mx-auto max-w-5xl px-6 py-8 space-y-6">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Link to={ROUTES.MISTAKES} className="hover:text-slate-700">返回列表</Link>
              {record && <Badge variant="outline">{statusLabels[record.status]}</Badge>}
            </div>
            <h1 className="text-2xl font-semibold tracking-tight">错题详情</h1>
          </div>
          {record && (
            <div className="flex gap-2">
              <Button
                variant="outline"
                disabled={addingFlashcard}
                onClick={async () => {
                  if (!mistakeId) return
                  setAddingFlashcard(true)
                  try {
                    const res = await generateFromMistakes({ mistakeRecordIds: [mistakeId] })
                    toast.success(`已生成 ${res.created} 张闪卡到「${res.deckName}」`)
                  } catch (err) {
                    toast.error(err instanceof Error ? err.message : "生成闪卡失败")
                  } finally {
                    setAddingFlashcard(false)
                  }
                }}
              >
                <Layers className="w-4 h-4 mr-2" />
                {addingFlashcard ? "生成中..." : "加入闪卡"}
              </Button>
              <Button variant="outline" onClick={handleDelete} disabled={saving}>
                删除
              </Button>
            </div>
          )}
        </div>

        {loading && <div className="text-sm text-slate-500">加载中...</div>}
        {error && <div className="text-sm text-red-500">{error}</div>}
        {tip && <div className="text-sm text-emerald-600">{tip}</div>}

        {record && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">题目内容</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* 如果是复合题型，显示组内容 */}
                {record.groupPassage && record.groupType && (
                  <div className="mb-4 p-3 rounded-md border border-blue-100 bg-blue-50">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm font-medium text-blue-700">
                        {record.groupType === 'reading_comp' && '阅读理解'}
                        {record.groupType === 'cloze' && '完形填空'}
                        {record.groupType === 'listening' && '听力'}
                        {record.groupType === 'seven_choose_five' && '七选五'}
                        {record.groupType === 'grammar_fill' && '语法填空'}
                        {!(record.groupType === 'reading_comp' || record.groupType === 'cloze' || 
                           record.groupType === 'listening' || record.groupType === 'seven_choose_five' || 
                           record.groupType === 'grammar_fill') && record.groupType}
                      </span>
                      {record.indexInGroup !== undefined && record.indexInGroup !== null && (
                        <span className="text-xs text-blue-600">
                          第 {(record.indexInGroup ?? 0) + 1} 题
                        </span>
                      )}
                    </div>
                    <div className="prose prose-sm max-w-none text-slate-700 bg-white p-3 rounded border border-slate-200 mb-3">
                      <LatexText>{record.groupPassage}</LatexText>
                    </div>
                  </div>
                )}
                <LatexText>{record.questionContent?.stem || "[公式/图片题]"}</LatexText>
                {record.questionOptions && record.questionOptions.length > 0 && (
                  <div className="grid gap-2">
                    {record.questionOptions.map((opt, idx) => (
                      <div key={idx} className="rounded-md border border-slate-200 px-3 py-2 text-sm">
                        <span className="font-medium mr-2">{opt.label || String.fromCharCode(65 + idx)}.</span>
                        <LatexText inline>{opt.content}</LatexText>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">解析与答案</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="text-sm text-slate-600">
                  <div className="font-medium text-slate-900 mb-1">正确答案</div>
                  <AnswerDisplay answer={record.correctAnswer} />
                </div>
                <div>
                  <div className="text-sm font-medium text-slate-900 mb-1">解析</div>
                  <LatexText>{record.explanation || "暂无解析"}</LatexText>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">历史作答</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {record.attempts.length === 0 && <div className="text-sm text-slate-500">暂无复盘记录</div>}
                {record.attempts.slice(0, 3).map((attempt) => (
                  <div key={attempt.id} className="rounded-md border border-slate-200 px-3 py-2 text-sm space-y-2">
                    <div className="flex items-center justify-between">
                      <span>{attempt.answeredAt ? new Date(attempt.answeredAt).toLocaleString() : "—"}</span>
                      <div className="flex items-center gap-2">
                        {attempt.disputeStatus === "disputed" && (
                          <Badge variant="destructive">有争议</Badge>
                        )}
                        <Badge variant="outline">{attempt.isCorrect ? "正确" : "错误"}</Badge>
                      </div>
                    </div>
                    {attempt.userAnswer && (
                      <div className="text-xs text-slate-500">
                        <AnswerDisplay answer={attempt.userAnswer} />
                      </div>
                    )}
                    {/* Error reasons */}
                    {!attempt.isCorrect && attempt.errorReasons && attempt.errorReasons.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {attempt.errorReasons.map((code) => {
                          const label = lookupErrorReasons.find(r => r.code === code)?.name || code
                          return <Badge key={code} variant="secondary" className="text-xs">{label}</Badge>
                        })}
                      </div>
                    )}
                    {/* Dispute info */}
                    {attempt.disputeStatus === "disputed" && attempt.disputeReason && (
                      <div className="text-xs text-amber-600">
                        申诉理由：{disputeReasonLabels[attempt.disputeReason] || attempt.disputeReason}
                      </div>
                    )}
                    {/* Dispute button for wrong, non-disputed attempts */}
                    {!attempt.isCorrect && attempt.disputeStatus !== "disputed" && (
                      <div>
                        {disputingAttemptId === attempt.id ? (
                          <div className="space-y-2 mt-2 p-2 bg-slate-50 rounded">
                            <div className="text-xs text-slate-600">选择申诉理由：</div>
                            <div className="flex flex-wrap gap-2">
                              {Object.entries(disputeReasonLabels).map(([code, label]) => (
                                <Button
                                  key={code}
                                  variant={disputeReason === code ? "default" : "outline"}
                                  size="sm"
                                  onClick={() => setDisputeReason(code)}
                                >
                                  {label}
                                </Button>
                              ))}
                            </div>
                            <div className="flex gap-2">
                              <Button size="sm" onClick={() => handleDispute(attempt.id)} disabled={!disputeReason || saving}>
                                提交申诉
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => { setDisputingAttemptId(null); setDisputeReason("") }}>
                                取消
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-amber-600 hover:text-amber-700"
                            onClick={() => setDisputingAttemptId(attempt.id)}
                          >
                            ⚠ 我认为判错了
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">复盘与状态</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Select value={status} onValueChange={(value) => setStatus(value as MistakeStatus)}>
                    <SelectTrigger>
                      <SelectValue placeholder="选择状态" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unreviewed">未复盘</SelectItem>
                      <SelectItem value="reviewed">已复盘</SelectItem>
                      <SelectItem value="mastered">已掌握</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="text-xs text-slate-500">
                    错题次数：{record.wrongCount}，最近错题：{record.lastWrongAt ? new Date(record.lastWrongAt).toLocaleString() : "—"}
                  </div>
                </div>

                {lookupErrorReasons.length > 0 && (
                  <div className="space-y-2">
                    <div className="text-sm font-medium text-slate-900">错误归因（可多选）</div>
                    <div className="flex flex-wrap gap-2">
                      {lookupErrorReasons.map((r) => (
                        <Button
                          key={r.code}
                          variant={selectedErrorReasons.includes(r.code) ? "default" : "outline"}
                          size="sm"
                          onClick={() => toggleErrorReason(r.code)}
                        >
                          {r.name}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}

                <Textarea placeholder="复盘备注" value={remark} onChange={(e) => setRemark(e.target.value)} />
                <div className="flex gap-2">
                  <Button onClick={handleSave} disabled={saving}>
                    保存
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  )
}
